"use client";

import React, { useState, useRef, useCallback } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Keyboard } from "swiper/modules";
import { Swiper as SwiperType } from "swiper";

import "swiper/css";

import { HomeBanner } from "@/packages/types/banner";
import HeroSlide from "./HeroSlide";
import HeroNavigation from "./HeroNavigation";
import HeroDots from "./HeroDots";
import { HEADER_V2_DESIGN_SYSTEM } from "@/components/header/v2/designTokens";

interface HeroCarouselV2Props {
  banners: HomeBanner[];
  className?: string;
}

const HeroCarouselV2: React.FC<HeroCarouselV2Props> = ({
  banners,
  className = "",
}) => {
  const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const handlePrev = useCallback(() => {
    swiperInstance?.slidePrev();
  }, [swiperInstance]);

  const handleNext = useCallback(() => {
    swiperInstance?.slideNext();
  }, [swiperInstance]);

  const handleDotClick = useCallback((index: number) => {
    swiperInstance?.slideToLoop(index);
  }, [swiperInstance]);

  const handleSlideChange = useCallback((swiper: SwiperType) => {
    setActiveIndex(swiper.realIndex);
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (swiperInstance?.autoplay && swiperInstance.autoplay.running) {
      swiperInstance.autoplay.stop();
    }
  }, [swiperInstance]);

  const handleMouseLeave = useCallback(() => {
    if (swiperInstance?.autoplay && !swiperInstance.autoplay.running) {
      swiperInstance.autoplay.start();
    }
  }, [swiperInstance]);

  if (!banners || banners.length === 0) return null;

  return (
    <div
      ref={containerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`relative w-full overflow-hidden group select-none ${HEADER_V2_DESIGN_SYSTEM.borderRadius.card} ${HEADER_V2_DESIGN_SYSTEM.elevation.card} ${className}`}
    >
      <Swiper
        modules={[Autoplay, Keyboard]}
        slidesPerView={1}
        spaceBetween={0}
        loop={banners.length > 1}
        keyboard={{ enabled: true }}
        autoplay={
          banners.length > 1
            ? { delay: 4000, disableOnInteraction: false }
            : false
        }
        onSwiper={setSwiperInstance}
        onSlideChange={handleSlideChange}
        className="w-full h-full"
      >
        {banners.map((banner, index) => (
          <SwiperSlide key={banner.id || index} className="w-full h-full">
            <HeroSlide
              imageUrl={banner.imageUrl}
              mobileUrl={banner.mobileUrl}
              navigationLink={banner.navigationLink}
              altText={`Featured Banner ${index + 1}`}
              priority={index === 0} // Only first banner is priority to optimize LCP
            />
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Navigation Arrows (Desktop overlay only, hidden on mobile) */}
      {banners.length > 1 && (
        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <HeroNavigation onPrev={handlePrev} onNext={handleNext} />
        </div>
      )}

      {/* Pagination Dots (Both desktop and mobile) */}
      {banners.length > 1 && (
        <HeroDots
          total={banners.length}
          activeIndex={activeIndex}
          onDotClick={handleDotClick}
        />
      )}
    </div>
  );
};

export default React.memo(HeroCarouselV2);
